/**
 * Copyright (c) Qwoach LLC 2025. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Artem Bogush <artem@qwoach.com> on 06.06.25
 *
 */
import ServiceItem from "@shared/Quote/ServiceItem";
import {classToClass, Type} from "@qwoach/class-transformer-wrapper";
import {CurrencyCodeEnum} from "@shared/Profile/CurrencyCodeEnum";
import {
	getDefaultRateTypeForServiceType,
	getSectionIdForServiceType,
	getServiceOptionsByType, QuoteRateType, rateServicesExtended, ServiceDescriptionEnum,
	ServiceType,
	STANDARD_RATE_TYPES,
	CUSTOM_RATE_TYPES,
	MAX_CUSTOM_RATES,
	BirthMemberMileageServices,
	isMileageServices,
} from "@shared/Quote/ServiceType";
import QuoteRateItem from "@shared/Quote/QuoteRateItem";

import {QuoteStatus} from "@shared/Quote/QuoteStatus";
import BaseIdObject from "@shared/BaseIdObject";
import dayjs from "dayjs";
import {CURRENCY_FORMATTING_OPTIONS_SHORT, currencyFormatted, defaultCurrencyCode} from "@shared/Profile/CurrencyItem";
import ContactDetailsItem from "@shared/Invoice/ContactDetailsItem";
import {DEFAULT_FY_START_DAY, DEFAULT_FY_START_MONTH, FY_ANCHOR_YEAR} from "@shared/SharedConstants";
import {LegacyQuoteSectionId, QuoteSectionId, resolveQuoteSectionId} from "@shared/Quote/QuoteSectionId";

const LEGACY_EMPTY_TRAVEL_TEMPLATE_SERVICE_TYPES = Object.freeze([
	ServiceType.TRAVEL_DISTANCE,
	ServiceType.TIME_BASED,
	ServiceType.PARKING_COST,
	ServiceType.FIXED_FEE,
	ServiceType.NON_CLINICAL_COSTS,
	ServiceType.VENUE_HIRE,
	ServiceType.MATERIALS_AND_EQUIPMENT,
]);

const LEGACY_SESSION_TYPES = new Set([
	"INITIAL_CONSULT_SESSION", "BEHAVIOR_TREE_SESSION", "DIRECT_TLSW_SESSION",
	"BIRTH_PARENT_1_SESSION", "BIRTH_PARENT_2_SESSION", "BIRTH_PARENT_3_SESSION", "BIRTH_PARENT_4_SESSION",
	"THERAPEUTIC_REFLECTIVE_SESSION", "PROFESSIONAL_MEETING", "CLINICAL_SUPERVISION", "EXTRA_SESSIONS",
]);

const LEGACY_SESSION_SECTION_BY_TYPE = Object.freeze({
	BIRTH_PARENT_1_SESSION: QuoteSectionId.INFO_GATHERING,
	BIRTH_PARENT_2_SESSION: QuoteSectionId.INFO_GATHERING,
	BIRTH_PARENT_3_SESSION: QuoteSectionId.INFO_GATHERING,
	BIRTH_PARENT_4_SESSION: QuoteSectionId.INFO_GATHERING,
	THERAPEUTIC_REFLECTIVE_SESSION: QuoteSectionId.OTHER_MEETINGS,
	PROFESSIONAL_MEETING: QuoteSectionId.OTHER_MEETINGS,
	EXTRA_SESSIONS: QuoteSectionId.OTHER_MEETINGS,
	CLINICAL_SUPERVISION: QuoteSectionId.NON_DIRECT_WORK,
});

const TEMPLATE_REMOVED_DISTANCE_SERVICE_TYPES = Object.freeze([
	ServiceType.ROUND_TRIP_MILEAGE,
	...BirthMemberMileageServices,
]);

const COMMISSION_DISTANCE_PARENT_SERVICE_TYPES = Object.freeze([
	"DIRECT_TLSW_SESSION",
	"INITIAL_CONSULT_SESSION",
	"BEHAVIOR_TREE_SESSION",
]);

const LEGACY_SECTION_IDS = Object.freeze(Object.values(LegacyQuoteSectionId));
const DEFAULT_BIRTH_FAMILY_MEMBER_COUNT = 2;

const PREPARATION_PARENT_SERVICE_TYPES = Object.freeze([
	"DIRECT_TLSW_SESSION",
	"INITIAL_CONSULT_SESSION",
	"BEHAVIOR_TREE_SESSION",
	"BIRTH_PARENT_1_SESSION",
	"BIRTH_PARENT_2_SESSION",
	"BIRTH_PARENT_3_SESSION",
	"BIRTH_PARENT_4_SESSION",
	"THERAPEUTIC_REFLECTIVE_SESSION",
	"PROFESSIONAL_MEETING",
	"CLINICAL_SUPERVISION",
	"EXTRA_SESSIONS",
]);

export default class QuoteItem extends BaseIdObject {

	title: string = null;

	contractStartDateMs: number = null;
	contractEndDateMs: number = null;
	commissionLocationRoundTripDistance: number = null;
	isFySplitEnabled: boolean = true;
	fyStartMs: number = null;

	refNumber: string = null;
	quoteStatus: QuoteStatus = QuoteStatus.DRAFT;

	createdAt: number = Date.now();
	sentAt: number = null;
	approvedAt: number = null

	@Type(() => ServiceItem)
	services: ServiceItem[] = [];

	@Type(() => QuoteRateItem)
	rates: QuoteRateItem[] = [];

	deletedStandardRateTypes: string[] = [];

	@Type(() => ContactDetailsItem)
	senderContactDetails: ContactDetailsItem = new ContactDetailsItem();

	currencyCode: CurrencyCodeEnum = defaultCurrencyCode;

	//the below props are only used for invoice rendering. Not saved to DB
	_totalPrice: string = null;
	_lineItemsFinal: ServiceItem[] = []
	_rateLineItemsFinal: QuoteRateItem[] = []


	constructor() {
		super();
		this.setupDefaultServices()
		this.ensureDefaultRatesIfEmpty()
	}

	static createDefaultTemplateQuote(userProfile = null) {
		let quoteItem = new QuoteItem()
		let defaultValues = userProfile?.getDefaultStore?.()?.defaultValues || {}
		quoteItem.applyDefaultValues(defaultValues)
		quoteItem.ensureDefaultRatesIfEmpty()
		quoteItem.cleanupLegacyEmptyStandaloneTravelTemplateServices()
		quoteItem.ensureServiceStructureMetadata()
		quoteItem.resetForTemplate()
		quoteItem.syncServiceRates()
		return quoteItem
	}

	cloneForTemplate() {
		let quoteItem = classToClass(this)
		quoteItem.migrateRelatedServiceModel()
		quoteItem.resetForTemplate()
		quoteItem.migrateLegacyRatesData()
		quoteItem.ensureStandardRates()
		quoteItem.ensureDefaultRatesIfEmpty()
		quoteItem.ensureServiceRatesAssigned()
		quoteItem.cleanupDeletedRatesReferences()
		quoteItem.cleanupLegacyEmptyStandaloneTravelTemplateServices()
		quoteItem.ensureServiceStructureMetadata()
		quoteItem.syncServiceRates()
		return quoteItem
	}

	resetForTemplate() {
		this.title = null
		this.refNumber = null
		this.resetObject()
		this.markAsDraft()
		this.createdAt = Date.now()
		this.resetCommissionSpecificDistanceTemplateData()
		this.resetRelatedServiceStatesForTemplate()
		this.getAllServiceItems().forEach(serviceItem => {
			if (isMileageServices(serviceItem.serviceType)) {
				serviceItem.quantity = null
			}
		})
		return this
	}

	resetRelatedServiceStatesForTemplate() {
		for (let serviceItem of this.getAllServiceItems()) {
			serviceItem.relatedServiceStates = null
		}
	}

	resetCommissionSpecificDistanceTemplateData() {
		this.commissionLocationRoundTripDistance = null
		this.removeParentBoundRoundTripMileageTemplateAddons()
		this.resetStandaloneTravelDistanceTemplateValues()
	}

	removeParentBoundRoundTripMileageTemplateAddons() {
		for (let parentServiceItem of this.getServicesMain()) {
			let nextSubItems = []
			for (let serviceItem of this.getChildServiceItems(parentServiceItem)) {
				let isTemplateRemovedDistanceService = TEMPLATE_REMOVED_DISTANCE_SERVICE_TYPES.includes(serviceItem?.serviceType)
				if (isTemplateRemovedDistanceService) {
					this.clearParentRelatedServiceState(parentServiceItem, ServiceType.ROUND_TRIP_MILEAGE)
					continue
				}
				nextSubItems.push(serviceItem)
			}
			parentServiceItem.subLineItems = nextSubItems
		}
	}

	resetStandaloneTravelDistanceTemplateValues() {
		for (let serviceItem of this.services || []) {
			if (serviceItem?.serviceType !== ServiceType.TRAVEL_DISTANCE) {
				continue
			}
			serviceItem.quantity = null
		}
	}

	clearParentRelatedServiceState(parentServiceItemOrId, childServiceType) {
		let parentServiceItem = typeof parentServiceItemOrId === "string"
			? this.getServiceItemById(parentServiceItemOrId)
			: parentServiceItemOrId
		let nextRelatedServiceStates = {
			...(parentServiceItem?.relatedServiceStates || {}),
		}
		if (!parentServiceItem || !nextRelatedServiceStates[childServiceType]) {
			return
		}
		delete nextRelatedServiceStates[childServiceType]
		parentServiceItem.relatedServiceStates = Object.keys(nextRelatedServiceStates).length > 0
			? nextRelatedServiceStates
			: null
	}

	migrateSessionServices({retainLegacyType = false} = {}) {
		for (let serviceItem of this.getAllServiceItems()) {
			if (!LEGACY_SESSION_TYPES.has(serviceItem.serviceType)) continue
			if (retainLegacyType) serviceItem.legacyServiceType = serviceItem.serviceType
			serviceItem.serviceType = ServiceType.SESSION
		}
	}

	isLegacySessionServiceType(serviceType) {
		return LEGACY_SESSION_TYPES.has(serviceType)
	}

	getBirthFamilySessions() {
		return this.getServicesMain().filter(item =>
			item.serviceType === ServiceType.SESSION && item.sectionId === QuoteSectionId.INFO_GATHERING)
	}

	syncBirthFamilyMembersQuantity(quantityNumber) {
		let count = Number(quantityNumber)
		if (!Number.isInteger(count) || count < 0) {
			return
		}
		let sessions = this.getBirthFamilySessions()
		for (let serviceItem of sessions.slice(count)) {
			this.deleteServiceItem(serviceItem)
		}
		let sessionOptions = getServiceOptionsByType(ServiceType.SESSION)
		for (let index = sessions.length; index < count; index++) {
			this.addService({
				...sessionOptions,
				sectionId: QuoteSectionId.INFO_GATHERING,
				title: `Birth Family Member #${index + 1} Session`,
				hasQuantityNumber: false,
				quantityNumber: 1,
				quantity: 120,
				quantityUnits: "minute",
			})
		}
	}

	setupDefaultServices(){
		this.services = this.services.filter(service => {
			let hasServiceDescription = !!ServiceDescriptionEnum[service.serviceType]
			let isLegacyRateService = rateServicesExtended.includes(service.serviceType)
			if (isLegacyRateService) {
				return false
			}
			if(!hasServiceDescription){
				console.warn(`QuoteItem: Service type ${service.serviceType} not found`);
			}
			return hasServiceDescription;
		});

		this.ensureStandardRates();

		let sessionOptions = getServiceOptionsByType(ServiceType.SESSION);
		this.addService({...sessionOptions, title: "Initial Consultation", hasQuantityNumber: false, quantityNumber: 1, quantity: 90});
		this.addService({...sessionOptions, title: "Behavior Tree Session", hasQuantityNumber: false, quantityNumber: 1, quantity: 90});
		let directTlswSession = this.addService(sessionOptions);
		this.addService({
			...getServiceOptionsByType(ServiceType.SESSION_PREPARATION_TIME),
			parentServiceItem: directTlswSession || null,
		});


		this.addService(getServiceOptionsByType(ServiceType.CODING));
		this.addService(getServiceOptionsByType(ServiceType.BIRTH_FAMILY_WORK_FORMULATION));

		this.addService(getServiceOptionsByType(ServiceType.TLSW_BOOK_WRITING));
		this.addService(getServiceOptionsByType(ServiceType.AAM_BOOK_WRITING));

		this.syncBirthFamilyMembersQuantity(DEFAULT_BIRTH_FAMILY_MEMBER_COUNT);

		this.migrateLegacyRatesData();
		this.ensureServiceRatesAssigned();
		this.ensureServiceStructureMetadata();
		this.syncServiceRates();
		this.cleanupDeletedRatesReferences();


	}

	migrateLegacyRatesData() {
		let directRateService = this.getServiceItemByType(ServiceType.DIRECT_SESSION_RATE)
		let adminRateService = this.getServiceItemByType(ServiceType.ADMIN_RATE)
		if (directRateService) {
			this.updateRate(QuoteRateType.DIRECT_SESSION_RATE, {itemPriceCents: directRateService.itemPriceCents})
		}
		if (adminRateService) {
			this.updateRate(QuoteRateType.ADMIN_RATE, {itemPriceCents: adminRateService.itemPriceCents})
		}
		this.services = this.services.filter(item => ![
			ServiceType.DIRECT_SESSION_RATE,
			ServiceType.ADMIN_RATE,
		].includes(item.serviceType))
		this.syncLegacyTravelRate()
	}

	migrateServiceTitles() {
		this.getAllServiceItems().forEach(serviceItem => serviceItem.migrateTitle?.())
	}

	migrateRateTitles() {
		this.rates.forEach(rateItem => rateItem.migrateTitle?.(rateType => this.getDefaultRateMetaByType(rateType)?.title))
	}

	migrateTitles() {
		this.migrateServiceTitles()
		this.migrateRateTitles()
	}

	migrateRelatedServiceModel() {
		this.ensureServiceStructureMetadata?.()
		this.ensureServiceRatesAssigned?.()
		this.cleanupDeletedRatesReferences?.()
		this.syncServiceRates?.()
	}

	migrateLegacySectionIds() {
		for (let serviceItem of this.getAllServiceItems()) {
			this.migrateLegacySectionIdForServiceItem(serviceItem)
		}
	}

	migrateLegacySectionIdForServiceItem(serviceItem) {
		if (!serviceItem?.serviceType) {
			return
		}
		if (!LEGACY_SECTION_IDS.includes(serviceItem.sectionId)) {
			return
		}
		serviceItem.sectionId = LEGACY_SESSION_SECTION_BY_TYPE[serviceItem.serviceType]
			|| getSectionIdForServiceType(serviceItem.serviceType)
	}

	migratePreparationWriteupUsageModel() {
		let preparationWriteupItems = this.getServiceItemsByType(ServiceType.SESSION_PREPARATION_TIME)
		for (let serviceItem of preparationWriteupItems) {
			serviceItem.serviceUsageType = getServiceOptionsByType(ServiceType.SESSION_PREPARATION_TIME)?.serviceUsageType
			serviceItem.hasQuantityNumber = true
		}
	}

	migrateLegacyCommissionMileageToParentBoundAddons() {
		let legacyMileageItems = this.getServiceItemsByType(ServiceType.ROUND_TRIP_MILEAGE, {topLevelOnly: true})
		let primaryLegacyMileageItem = legacyMileageItems[0] || null
		if (!primaryLegacyMileageItem) {
			return
		}
		let legacyDistance = Number(primaryLegacyMileageItem.quantity) || 0
		if (legacyDistance > 0) {
			this.commissionLocationRoundTripDistance = legacyDistance
		}
		for (let parentServiceType of COMMISSION_DISTANCE_PARENT_SERVICE_TYPES) {
			let parentServiceItem = this.getServiceItemByType(parentServiceType)
			if (!parentServiceItem) {
				continue
			}
			let mileageItem = this.getServiceItemByType(ServiceType.ROUND_TRIP_MILEAGE, {
				parentServiceItem,
			})
			if (!mileageItem) {
				mileageItem = this.addService(this.buildMigratedParentBoundRelatedServiceOptions(
					primaryLegacyMileageItem,
					ServiceType.ROUND_TRIP_MILEAGE,
					parentServiceItem
				))
			}
			if (!mileageItem) {
				continue
			}
			if (!mileageItem.quantity && legacyDistance > 0) {
				mileageItem.quantity = legacyDistance
			}
			mileageItem.isGlobalDefaultOverridden = false
		}
		for (let legacyMileageItem of legacyMileageItems) {
			this.deleteServiceItem(legacyMileageItem)
		}
	}

	migrateLegacyBirthMileageToParentBoundAddons() {
		let legacyBirthMileageToParentType = {
			[ServiceType.BIRTH_PARENT_1_MILEAGE]: "BIRTH_PARENT_1_SESSION",
			[ServiceType.BIRTH_PARENT_2_MILEAGE]: "BIRTH_PARENT_2_SESSION",
			[ServiceType.BIRTH_PARENT_3_MILEAGE]: "BIRTH_PARENT_3_SESSION",
			[ServiceType.BIRTH_PARENT_4_MILEAGE]: "BIRTH_PARENT_4_SESSION",
		}
		let migratedServiceItems = []
		let legacyServiceItems = []
		for (let serviceItem of this.services || []) {
			let parentServiceType = legacyBirthMileageToParentType[serviceItem?.serviceType]
			if (!parentServiceType) {
				continue
			}
			let parentServiceItem = this.getServiceItemByType(parentServiceType)
			if (!parentServiceItem) {
				continue
			}
			let migratedServiceItem = this.getServiceItemByType(ServiceType.ROUND_TRIP_MILEAGE, {
				parentServiceItem,
			})
			if (!migratedServiceItem) {
				migratedServiceItem = this.addService(this.buildMigratedParentBoundRelatedServiceOptions(
					serviceItem,
					ServiceType.ROUND_TRIP_MILEAGE,
					parentServiceItem
				))
			}
			migratedServiceItems.push(migratedServiceItem)
			legacyServiceItems.push(serviceItem)
		}
		for (let legacyServiceItem of legacyServiceItems) {
			this.deleteServiceItem(legacyServiceItem)
		}
		if (migratedServiceItems.length > 0) {
			this.ensureServiceStructureMetadata?.()
			this.syncServiceRates?.()
		}
	}

	migrateLegacyPreparationToParentBoundAddons() {
		let legacyPreparationItems = this.getServiceItemsByType(ServiceType.SESSION_PREPARATION_TIME, {topLevelOnly: true})
		let migratedServiceItems = []
		let legacyServiceItems = []
		for (let serviceItem of legacyPreparationItems) {
			let parentServiceItem = this.getPreferredPreparationMigrationParentServiceItem()
			if (!parentServiceItem) {
				continue
			}
			let migratedServiceItem = this.getServiceItemByType(ServiceType.SESSION_PREPARATION_TIME, {
				parentServiceItem,
			})
			if (!migratedServiceItem) {
				migratedServiceItem = this.addService(this.buildMigratedParentBoundRelatedServiceOptions(
					serviceItem,
					ServiceType.SESSION_PREPARATION_TIME,
					parentServiceItem
				))
			}
			migratedServiceItems.push(migratedServiceItem)
			legacyServiceItems.push(serviceItem)
		}
		for (let legacyServiceItem of legacyServiceItems) {
			this.deleteServiceItem(legacyServiceItem)
		}
		if (migratedServiceItems.length > 0) {
			this.ensureServiceStructureMetadata?.()
			this.syncServiceRates?.()
		}
	}

	getPreferredPreparationMigrationParentServiceItem() {
		let directTlswSession = this.getServiceItemByType("DIRECT_TLSW_SESSION")
		if (directTlswSession) {
			return directTlswSession
		}
		let availableParentServiceItems = PREPARATION_PARENT_SERVICE_TYPES
			.map(serviceType => this.getServiceItemByType(serviceType))
			.filter(item => !!item)
		let selectedParentServiceItem = availableParentServiceItems.length === 1 ? availableParentServiceItems[0] : null
		return selectedParentServiceItem
	}

	buildMigratedParentBoundRelatedServiceOptions(sourceServiceItem, targetServiceType, parentServiceItem) {
		let migratedOptions = {
			...getServiceOptionsByType(targetServiceType),
			parentServiceItem,
			title: sourceServiceItem.title,
			rateType: sourceServiceItem.rateType,
			quantity: sourceServiceItem.quantity,
			quantityNumber: sourceServiceItem.quantityNumber,
			itemPriceCents: sourceServiceItem.itemPriceCents,
			currencyCode: sourceServiceItem.currencyCode,
			fy1UsageValue: sourceServiceItem.fy1UsageValue,
			isGlobalDefaultOverridden: false,
		}
		return migratedOptions
	}

	ensureServiceStructureMetadata() {
		this.services = Array.isArray(this.services) ? this.services : []
		this.normalizeNestedServices()
		for (let serviceItem of this.getAllServiceItems()) {
			if (!serviceItem) {
				continue
			}
			if (!serviceItem.sectionId) {
				serviceItem.sectionId = getSectionIdForServiceType(serviceItem.serviceType)
			}
			serviceItem.syncDerivedConfig?.()
		}
	}

	normalizeNestedServices() {
		this.services = Array.isArray(this.services) ? this.services : []
		let topLevelServices = []
		let flatChildItemsByParentId = {}

		for (let serviceItem of this.services) {
			if (!serviceItem) {
				continue
			}
			serviceItem.subLineItems = Array.isArray(serviceItem.subLineItems) ? serviceItem.subLineItems : []
			let legacyParentServiceId = serviceItem.parentServiceId
			if (legacyParentServiceId) {
				if (!flatChildItemsByParentId[legacyParentServiceId]) {
					flatChildItemsByParentId[legacyParentServiceId] = []
				}
				flatChildItemsByParentId[legacyParentServiceId].push(serviceItem)
				delete serviceItem.parentServiceId
				continue
			}
			topLevelServices.push(serviceItem)
		}

		this.services = topLevelServices
		for (let parentServiceItem of this.getAllServiceItems()) {
			parentServiceItem.subLineItems = Array.isArray(parentServiceItem.subLineItems) ? parentServiceItem.subLineItems : []
			let migratedChildren = flatChildItemsByParentId[parentServiceItem.id] || []
			if (migratedChildren.length === 0) {
				continue
			}
			let existingChildMap = Object.fromEntries(
				parentServiceItem.subLineItems
					.filter(item => !!item?.id)
					.map(item => [item.id, true])
			)
			for (let childServiceItem of migratedChildren) {
				if (existingChildMap[childServiceItem.id]) {
					continue
				}
				parentServiceItem.addSubItems(childServiceItem)
			}
		}
	}

	walkServiceItems(visitor, serviceItems = this.services, parentServiceItem = null) {
		for (let serviceItem of serviceItems || []) {
			if (!serviceItem) {
				continue
			}
			visitor(serviceItem, parentServiceItem)
			this.walkServiceItems(visitor, serviceItem.subLineItems, serviceItem)
		}
	}

	getAllServiceItems() {
		let serviceItems = []
		this.walkServiceItems(serviceItem => {
			serviceItems.push(serviceItem)
		})
		return serviceItems
	}

	getParentServiceItem(targetServiceItem) {
		if (!targetServiceItem?.id) {
			return null
		}
		let parentServiceItem = null
		this.walkServiceItems((serviceItem, parentItem) => {
			if (parentServiceItem || !parentItem) {
				return
			}
			if (serviceItem.id === targetServiceItem.id) {
				parentServiceItem = parentItem
			}
		})
		return parentServiceItem
	}

	getChildServiceItems(parentServiceItem) {
		return Array.isArray(parentServiceItem?.subLineItems) ? parentServiceItem.subLineItems : []
	}

	getChildServiceItemByType(parentServiceItem, serviceType) {
		return this.getChildServiceItems(parentServiceItem).find(item => item.serviceType === serviceType) || null
	}

	filterSplit(filterFn = (item) => true) {
		let matched = [];
		let notMatched = [];
		this.services.forEach(item => {
			if(filterFn(item)) {
				matched.push(item);
			} else {
				notMatched.push(item);
			}
		})
		return [matched, notMatched]
	}

	ensureStandardRates() {
		this.rates = Array.isArray(this.rates) ? this.rates : []
		this.deletedStandardRateTypes = this.getNormalizedDeletedStandardRateTypes()
		for (let rateType of STANDARD_RATE_TYPES) {
			if (this.deletedStandardRateTypes.includes(rateType)) {
				continue
			}
			let rateItem = this.getRateByType(rateType)
			if (!rateItem) {
				this.rates.push(this.createRateByType(rateType))
			}
		}
	}

	ensureDefaultRatesIfEmpty() {
		let hasVisibleRates = this.getVisibleRates().length > 0
		if (hasVisibleRates) {
			return
		}
		this.deletedStandardRateTypes = []
		this.rates = STANDARD_RATE_TYPES.map(rateType => this.createRateByType(rateType))
		this.syncQuoteAfterDefaultRatesSeeded()
	}

	syncQuoteAfterDefaultRatesSeeded() {
		this.ensureServiceRatesAssigned()
		this.cleanupDeletedRatesReferences()
		this.syncServiceRates()
	}

	getNormalizedDeletedStandardRateTypes() {
		let deletedRateTypes = Array.isArray(this.deletedStandardRateTypes) ? this.deletedStandardRateTypes : []
		return [...new Set(deletedRateTypes.filter(rateType => STANDARD_RATE_TYPES.includes(rateType)))]
	}

	createRateByType(rateType, options = {}) {
		let defaults = this.getDefaultRateMetaByType(rateType)
		return new QuoteRateItem({
			id: rateType,
			rateType,
			isStandard: STANDARD_RATE_TYPES.includes(rateType),
			...defaults,
			...options,
		})
	}

	getDefaultRateMetaByType(rateType) {
		switch (rateType) {
			case QuoteRateType.DIRECT_SESSION_RATE:
				return {title: "Direct Contact Hourly Rate", itemPriceUnits: "hour", itemPriceCents: 7000}
			case QuoteRateType.ADMIN_RATE:
				return {title: "Non-contact Hourly Rate", itemPriceUnits: "hour", itemPriceCents: 5000}
			case QuoteRateType.TRAVEL_RATE:
				return {title: "Travel Distance Rate", itemPriceUnits: "mile", itemPriceCents: 25}
			default:
				return {title: "Custom Rate", itemPriceUnits: ""}
		}
	}

	getVisibleRates() {
		let rates = Array.isArray(this.rates) ? this.rates : []
		return rates.filter(rateItem => !!rateItem && !!rateItem.rateType)
	}

	getVisibleRatesSorted() {
		let visibleRates = this.getVisibleRates()
		let standard = visibleRates.filter(item => item.isStandard)
		let custom = visibleRates.filter(item => !item.isStandard)
		return [...standard, ...custom]
	}

	getRateByType(rateType) {
		let rates = Array.isArray(this.rates) ? this.rates : []
		return rates.find(item => item.rateType === rateType) || null
	}

	getRateOptionItems() {
		return this.getVisibleRatesSorted().map(rateItem => ({
			label: rateItem.getLineItemPriceFormatted(this.currencyCode),
			value: rateItem.rateType,
			description: this.getRateDisplayTitle(rateItem),
			itemPriceCents: rateItem.itemPriceCents,
			itemPriceUnits: rateItem.itemPriceUnits,
			title: rateItem.title,
			isStandard: rateItem.isStandard,
		}))
	}

	hasCustomRateSlots() {
		return this.getVisibleRates().filter(item => !item.isStandard).length < MAX_CUSTOM_RATES
	}

	getNextAvailableCustomRateType() {
		let used = {}
		this.getVisibleRates().forEach(item => {
			used[item.rateType] = true
		})
		return CUSTOM_RATE_TYPES.find(type => !used[type]) || null
	}

	getCustomRateFallbackTitle(rateType) {
		return "Additional Service Rate"
	}

	getRateTitleFallback(rateType) {
		let defaultTitle = this.getDefaultRateMetaByType(rateType)?.title
		if (defaultTitle) {
			return defaultTitle
		}
		return this.getCustomRateFallbackTitle(rateType)
	}

	getServiceCustomRateFallbackTitle(serviceTitle = "") {
		let normalizedTitle = String(serviceTitle || "").trim()
		if (!normalizedTitle) {
			return this.getCustomRateFallbackTitle()
		}
		return `${normalizedTitle} Rate`
	}

	getRateDisplayTitle(rateItem) {
		if (!rateItem) {
			return ""
		}
		if (rateItem.title) {
			return rateItem.title
		}
		return this.getRateTitleFallback(rateItem.rateType)
	}

	addCustomRate({title, itemPriceCents, fallbackTitle} = {}) {
		let nextRateType = this.getNextAvailableCustomRateType()
		if (!nextRateType) {
			return null
		}
		let rateItem = this.createRateByType(nextRateType, {
			title: String(title || "").trim() || String(fallbackTitle || "").trim() || this.getCustomRateFallbackTitle(nextRateType),
			itemPriceCents,
			isStandard: false,
		})
		this.rates.push(rateItem)
		return rateItem
	}

	updateRate(rateType, options = {}) {
		let rateItem = this.getRateByType(rateType)
		if (!rateItem) {
			return null
		}
		if (options.title !== undefined) {
			rateItem.title = String(options.title || "").trim() || this.getRateTitleFallback(rateType)
		}
		if (options.itemPriceCents !== undefined && options.itemPriceCents !== null) {
			rateItem.itemPriceCents = Number(options.itemPriceCents) || 0
		}
		return rateItem
	}

	deleteRate(rateType, replacementRateType = null) {
		let rateItem = this.getRateByType(rateType)
		if (!rateItem) {
			return false
		}
		if (replacementRateType) {
			this.reassignServicesRateType(rateType, replacementRateType)
		}
		if (rateItem.isStandard) {
			this.deletedStandardRateTypes = [
				...this.getNormalizedDeletedStandardRateTypes(),
				rateType,
			]
		}
		this.rates = this.rates.filter(item => item.rateType !== rateType)
		this.cleanupDeletedRatesReferences()
		return true
	}

	getServicesByRateType(rateType) {
		return this.getAllServiceItems().filter(serviceItem => serviceItem.rateType === rateType)
	}

	reassignServicesRateType(fromRateType, toRateType) {
		for (let serviceItem of this.getServicesByRateType(fromRateType)) {
			serviceItem.rateType = toRateType
		}
	}

	cleanupDeletedRatesReferences() {
		let visibleRateMap = {}
		this.getVisibleRates().forEach(rateItem => {
			visibleRateMap[rateItem.rateType] = true
		})
		for (let serviceItem of this.getAllServiceItems()) {
			if (serviceItem.rateType && !visibleRateMap[serviceItem.rateType]) {
				serviceItem.rateType = this.getPreferredRateTypeForServiceType(serviceItem.serviceType)
			}
		}
	}

	ensureServiceRatesAssigned() {
		for (let serviceItem of this.getAllServiceItems()) {
			if (serviceItem._skipRateSync) {
				continue
			}
			if (serviceItem.rateType && this.getRateByType(serviceItem.rateType)) {
				continue
			}
			serviceItem.rateType = this.getPreferredRateTypeForServiceType(serviceItem.serviceType)
		}
	}

	getPreferredRateTypeForServiceType(serviceType) {
		let defaultRateType = getDefaultRateTypeForServiceType(serviceType)
		if (defaultRateType && this.getRateByType(defaultRateType)) {
			return defaultRateType
		}
		let serviceOptions = getServiceOptionsByType(serviceType) || {}
		let preferredUnits = serviceOptions.itemPriceUnits || serviceOptions.quantityUnits || null
		if (preferredUnits) {
			let matchingRate = this.getVisibleRatesSorted().find(rateItem => rateItem.itemPriceUnits === preferredUnits)
			if (matchingRate) {
				return matchingRate.rateType
			}
		}
		return this.getVisibleRatesSorted()[0]?.rateType || null
	}

	syncServiceRates() {
		for (let serviceItem of this.getAllServiceItems()) {
			if (serviceItem._skipRateSync) {
				continue
			}
			if (serviceItem.isUsageByPrice?.()) {
				continue
			}
			if (!serviceItem.rateType) {
				continue
			}
			let rateItem = this.getRateByType(serviceItem.rateType)
			if (!rateItem) {
				continue
			}
			serviceItem.itemPriceCents = rateItem.itemPriceCents
		}
	}

	syncLegacyTravelRate() {
		let tripRateService = this.services.find(item => item.serviceType === ServiceType.TRIP_RATE)
		if (!tripRateService) {
			return
		}
		this.updateRate(QuoteRateType.TRAVEL_RATE, {itemPriceCents: tripRateService.itemPriceCents})
		let mileageServices = [
			ServiceType.ROUND_TRIP_MILEAGE,
			ServiceType.BIRTH_PARENT_1_MILEAGE,
			ServiceType.BIRTH_PARENT_2_MILEAGE,
			ServiceType.BIRTH_PARENT_3_MILEAGE,
			ServiceType.BIRTH_PARENT_4_MILEAGE,
		]
		for (let serviceType of mileageServices) {
			let serviceItems = this.getServiceItemsByType(serviceType)
			if (serviceItems.length === 0) {
				continue
			}
			for (let serviceItem of serviceItems) {
				serviceItem.rateType = QuoteRateType.TRAVEL_RATE
			}
		}
		this.services = this.services.filter(item => item.serviceType !== ServiceType.TRIP_RATE)
	}

	getServicesMain(){
		return this.services
	}


	isApproved() {
		return this.quoteStatus === QuoteStatus.APPROVED;
	}

	isRejected() {
		return this.quoteStatus === QuoteStatus.REJECTED;
	}

	isSent() {
		return this.quoteStatus === QuoteStatus.SENT;
	}

	isDraft() {
		return this.quoteStatus === QuoteStatus.DRAFT;
	}

	getServiceItems() {
		return this.services
	}

	setupRefNumber(){
		if (!this.refNumber) {
			let date = dayjs();
			this.refNumber = `Q-${date.format("YYYYMMDD")}-${date.format("HH")}`;
		}
	}

	applyDefaultValues(defaultValues = {}){
		console.log("QuoteItem.applyDefaultValues: defaultValues = ", defaultValues);
		this.ensureStandardRates()
		Object.keys(defaultValues).forEach((key) => {
			let rateItem = this.getRateByType(key)
			if (rateItem) {
				let rateValues = defaultValues[key] || {}
				if (rateValues.itemPriceCents !== undefined && rateValues.itemPriceCents !== null) {
					rateItem.itemPriceCents = rateValues.itemPriceCents
				}
			}
			let serviceItem = this.getServiceItemByType(key)
			if(serviceItem) {
				let values = defaultValues[key] || {}
				serviceItem.quantity = values.quantity || serviceItem.quantity
				serviceItem.quantityNumber = values.quantityNumber || serviceItem.quantity;
				serviceItem.itemPriceCents = values.itemPriceCents || serviceItem.itemPriceCents
			}
		})
	}

	cleanupLegacyEmptyStandaloneTravelTemplateServices() {
		this.services = (this.services || []).filter(serviceItem => {
			let isLegacyEmptyTravelDefault = this.isLegacyEmptyStandaloneTravelTemplateService(serviceItem)
			return !isLegacyEmptyTravelDefault
		})
	}

	isLegacyEmptyStandaloneTravelTemplateService(serviceItem) {
		let isTravelTemplateType = LEGACY_EMPTY_TRAVEL_TEMPLATE_SERVICE_TYPES.includes(serviceItem?.serviceType)
		let resolvedSectionId = resolveQuoteSectionId(serviceItem?.sectionId)
		let hasParentBinding = !!this.getParentServiceItem(serviceItem)
		let hasDescription = !!String(serviceItem?.description || "").trim()
		let totalPriceCents = Number(serviceItem?.getTotalPriceCents?.()) || 0
		let hasMeaningfulValue = totalPriceCents > 0
		let isLegacyEmptyTravelDefault = isTravelTemplateType
			&& resolvedSectionId === QuoteSectionId.TRAVEL
			&& !hasParentBinding
			&& !hasDescription
			&& !hasMeaningfulValue
		return isLegacyEmptyTravelDefault
	}


	addService(options = {}, parentServiceItem = null){
		let service = options instanceof ServiceItem ? options : null
		let serviceOptions = service ? {...service} : {...options}
		parentServiceItem = parentServiceItem || serviceOptions.parentServiceItem || null
		delete serviceOptions.parentServiceItem
		delete serviceOptions.parentServiceId
		if(!(service?.serviceType || serviceOptions.serviceType) || !ServiceType[service?.serviceType || serviceOptions.serviceType]) {
			console.warn(`Service ${service?.serviceType || serviceOptions.serviceType} is not a valid service type`);
			return null
		}
		if (!service) {
			service = new ServiceItem(serviceOptions);
		}
		if (service.usesRate?.() && (!service.rateType || !this.getRateByType(service.rateType))) {
			service.rateType = this.getPreferredRateTypeForServiceType(service.serviceType)
		}
		if (service.sectionId === undefined || service.sectionId === null) {
			service.sectionId = getSectionIdForServiceType(service.serviceType)
		}
		service.syncDerivedConfig?.()
		if (parentServiceItem) {
			parentServiceItem.addSubItems(service);
		} else {
			this.services.push(service);
		}
		if (service.rateType) {
			let rateItem = this.getRateByType(service.rateType)
			if (rateItem) {
				service.itemPriceCents = rateItem.itemPriceCents
			}
		}
		return service;
	}

	createConfiguredServiceItem({
		serviceType,
		title = "",
		sectionId,
		rateType = null,
		quantity = null,
		quantityNumber = null,
		itemPriceCents = null,
	} = {}) {
		let serviceOptions = getServiceOptionsByType(serviceType)
		let serviceItem = this.addService({
			...serviceOptions,
			serviceType,
			title,
			sectionId: resolveQuoteSectionId(sectionId),
			rateType,
			quantity,
			quantityNumber,
			itemPriceCents,
		})
		if (serviceItem?.isUsageByPrice?.()) {
			serviceItem.itemPriceCents = Number(itemPriceCents) || 0
		}
		this.syncServiceRates()
		return serviceItem
	}

	deleteServiceItem(serviceItem) {
		let parentServiceItem = this.getParentServiceItem(serviceItem)
		if (parentServiceItem) {
			parentServiceItem.subLineItems = this.getChildServiceItems(parentServiceItem)
				.filter(item => item.id !== serviceItem.id && item !== serviceItem)
			return true
		}
		return this.services.deleteItem(item => item.id === serviceItem.id || item === serviceItem);
	}

	getServiceTypeArray() {
		const services = {};
		for(let item of this.getAllServiceItems()) {
			services[item.serviceType] = true;
		}
		let res = Object.keys(services)
		return res;
	}

	getServiceItemById(serviceItemId) {
		return this.getAllServiceItems().find(item => item.id === serviceItemId) || null
	}

	getServiceItemsByType(serviceType, options = {}) {
		if(!serviceType || !ServiceType[serviceType]) {
			console.warn(`QuoteItem.getServiceItemsByType: Service ${serviceType} is not a valid service type`);
			return [];
		}
		let {parentServiceItem = null, topLevelOnly = false} = options
		let sourceArray = topLevelOnly ? this.getServicesMain() : this.getAllServiceItems()
		return sourceArray.filter(item => {
			if (item.serviceType !== serviceType) {
				return false
			}
			if (parentServiceItem) {
				return this.getParentServiceItem(item)?.id === parentServiceItem.id
			}
			return true
		})
	}

	getServiceItemByType(serviceType, options = {}) {
		if(!serviceType || !ServiceType[serviceType]) {
			console.warn(`QuoteItem.getServiceItemByType: Service ${serviceType} is not a valid service type`);
			return null;
		}
		let matchingItems = this.getServiceItemsByType(serviceType, options)
		if (matchingItems.length === 0) {
			return null
		}
		return matchingItems[0] || null
	}
	getServiceItemsByTypeArray(serviceTypeArray) {
		return this.getAllServiceItems().filter(item => serviceTypeArray.includes(item.serviceType))
	}


	getTotalAmountCents(){
		let total = 0
		for(let serviceItem of this.getServicesMain()){
			let totalLocal = serviceItem.getTotalPriceCents()
			total += totalLocal
			// console.log("QuoteItem.getTotalAmountCents: totalLocal = ", totalLocal, ", total = ", total, ", serviceItem = ", serviceItem);
		}
		return total
	}

	getTotalAmount(){
		return this.getTotalAmountCents() / 100
	}

	getTotalPriceFormatted(currencyCode = this.currencyCode) {
		return currencyFormatted(currencyCode, this.getTotalAmount(), CURRENCY_FORMATTING_OPTIONS_SHORT);
	}

	getFy1TotalCents() {
		let totalCents = 0;
		for (let serviceItem of this.getServicesMain()) {
			totalCents += serviceItem.getFy1AmountCents();
		}
		return totalCents;
	}

	getFy2TotalCents() {
		let totalCents = 0;
		for (let serviceItem of this.getServicesMain()) {
			totalCents += serviceItem.getFy2AmountCents();
		}
		return totalCents;
	}

	getFy1TotalFormatted(currencyCode = this.currencyCode) {
		return currencyFormatted(currencyCode, this.getFy1TotalCents() / 100, CURRENCY_FORMATTING_OPTIONS_SHORT);
	}

	getFy2TotalFormatted(currencyCode = this.currencyCode) {
		return currencyFormatted(currencyCode, this.getFy2TotalCents() / 100, CURRENCY_FORMATTING_OPTIONS_SHORT);
	}

	getFinancialYearQuoteItems() {
		return {
			fy1: this.getFinancialYearQuoteItem("fy1"),
			fy2: this.getFinancialYearQuoteItem("fy2"),
		}
	}

	getFinancialYearQuoteItem(financialYearKey = "fy1") {
		let projectedQuoteItem = classToClass(this)
		let financialYearRanges = this.getFinancialYearRanges()
		let financialYearRange = financialYearRanges[financialYearKey] || null

		projectedQuoteItem.services = this.getServicesMain().map(serviceItem =>
			this.buildFinancialYearProjectedServiceItem(serviceItem, {
				financialYearKey,
			})
		).filter(Boolean)
		projectedQuoteItem.isFySplitEnabled = false
		projectedQuoteItem._financialYearProjectionKey = financialYearKey
		projectedQuoteItem._financialYearRange = financialYearRange
		projectedQuoteItem.ensureServiceStructureMetadata()
		projectedQuoteItem.syncServiceRates()

		return projectedQuoteItem
	}

	buildFinancialYearProjectedServiceItem(serviceItem, {
		financialYearKey = "fy1",
		parentUsageRatio = null,
	} = {}) {
		let totalUsageValue = Number(serviceItem.getUsageValue()) || 0
		let projectedUsageValue = this.getFinancialYearProjectedUsageValue(serviceItem, {
			financialYearKey,
			parentUsageRatio,
		})
		if (projectedUsageValue <= 0) {
			return null
		}

		let projectedServiceItem = classToClass(serviceItem)
		let usageRatio = totalUsageValue > 0 ? projectedUsageValue / totalUsageValue : 0

		projectedServiceItem.setUsageValue(projectedUsageValue)
		projectedServiceItem.fy1UsageValue = null
		if (projectedServiceItem.isUsageByPrice()) {
			projectedServiceItem._skipRateSync = true
			projectedServiceItem.rateType = null
		}
		projectedServiceItem.subLineItems = this.getChildServiceItems(serviceItem).map(childServiceItem =>
			this.buildFinancialYearProjectedServiceItem(childServiceItem, {
				financialYearKey,
				parentUsageRatio: usageRatio,
			})
		).filter(Boolean)

		return new ServiceItem(projectedServiceItem)
	}

	getFinancialYearProjectedUsageValue(serviceItem, {
		financialYearKey = "fy1",
		parentUsageRatio = null,
	} = {}) {
		let totalUsageValue = Number(serviceItem.getUsageValue()) || 0
		if (totalUsageValue <= 0) {
			return 0
		}
		if (parentUsageRatio !== null && parentUsageRatio !== undefined) {
			return totalUsageValue * parentUsageRatio
		}
		if (financialYearKey === "fy2") {
			return serviceItem.getFy2UsageValue()
		}
		return serviceItem.getFy1UsageValue()
	}

	getFinancialYearStartMs(dateMs) {
		let date = dayjs(dateMs || Date.now());
		let year = date.year();
		let base = this.fyStartMs
			? dayjs(this.fyStartMs)
			: dayjs()
				.year(FY_ANCHOR_YEAR)
				.month(DEFAULT_FY_START_MONTH - 1)
				.date(DEFAULT_FY_START_DAY);
		let month = base.month() + 1;
		let day = base.date();
		let monthPadded = String(month).padStart(2, "0");
		let dayPadded = String(day).padStart(2, "0");
		let fyStart = dayjs(`${year}-${monthPadded}-${dayPadded}`);
		if (date.isBefore(fyStart, "day")) {
			fyStart = fyStart.subtract(1, "year");
		}
		return fyStart.valueOf();
	}

	getFinancialYearRange(dateMs) {
		let startMs = this.getFinancialYearStartMs(dateMs);
		let start = dayjs(startMs);
		let end = start.add(1, "year").subtract(1, "day");
		return {
			startMs: start.valueOf(),
			endMs: end.valueOf(),
		};
	}

	getFinancialYearRanges(dateMs = this.contractStartDateMs || Date.now()) {
		let fy1 = this.getFinancialYearRange(dateMs);
		let fy2StartMs = dayjs(fy1.startMs).add(1, "year").valueOf();
		let fy2 = this.getFinancialYearRange(fy2StartMs);
		return {fy1, fy2};
	}


	markAsSent() {
		this.quoteStatus = QuoteStatus.SENT;
		this.sentAt = Date.now()
	}

	markAsApproved() {
		this.quoteStatus = QuoteStatus.APPROVED;
		this.approvedAt = Date.now()
	}

	markAsRejected() {
		this.quoteStatus = QuoteStatus.REJECTED;
	}

	markAsDraft() {
		this.quoteStatus = QuoteStatus.DRAFT;
		this.sentAt = null
		this.approvedAt = null
	}

	sentOnFormatted(format) {
		if(!this.sentAt) {
			return ""
		}
		format = format || "MMM DD, YYYY"
		return dayjs(this.sentAt).format(format)
	}


	computeValues(commissionDetails = null) {
		this.migrateLegacyRatesData()
		this.ensureServiceStructureMetadata()
		this.ensureServiceRatesAssigned()
		this.cleanupDeletedRatesReferences()
		this.syncServiceRates()

		this.rates.forEach(rateItem => {
			rateItem.computeValues(commissionDetails);
		})
		this.getAllServiceItems().forEach(serviceItem => {
			if (!serviceItem.sectionId) {
				serviceItem.sectionId = getSectionIdForServiceType(serviceItem.serviceType)
			}
			serviceItem.syncDerivedConfig?.()
			serviceItem.computeValues(commissionDetails);
			serviceItem.normalizeFySplit?.();
		});
		this._totalPrice = this.getTotalPriceFormatted()

		let primaryRates = this.getVisibleRatesSorted()
		let services = this.getServicesMain().filter((lineItem) => lineItem.getTotalPriceCents() !== 0)

		this._rateLineItemsFinal = primaryRates
		this._lineItemsFinal = services
	}

}
